import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Wifi, WifiOff, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { getCategory } from "../lib/notificationCategories";

const SSE_RETRY_DELAY_MS = 5000;
const POLL_FALLBACK_MS = 30000; // filet de sécurité si SSE échoue

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [liveConnected, setLiveConnected] = useState(false);
  const [prefs, setPrefs] = useState({ muted_categories: [], muted_toast_categories: [], limit_to_my_brands: false });
  const sseRef = useRef(null);
  const pollRef = useRef(null);
  const retryTimerRef = useRef(null);
  const prefsRef = useRef(prefs);

  useEffect(() => { prefsRef.current = prefs; }, [prefs]);

  const loadFull = async () => {
    try {
      const [list, count, pf] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
        api.get("/notifications/preferences"),
      ]);
      setItems(list.data || []);
      setUnread(count.data?.count || 0);
      setPrefs(pf.data || { muted_categories: [], muted_toast_categories: [], limit_to_my_brands: false });
    } catch {
      // ignore
    }
  };

  // Ouvre / ré-ouvre la connexion SSE.
  const openStream = () => {
    if (sseRef.current) {
      try { sseRef.current.close(); } catch { /* noop */ }
      sseRef.current = null;
    }
    const token = localStorage.getItem("plania_token");
    if (!token || !BACKEND_URL) return;
    const url = `${BACKEND_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    sseRef.current = es;

    es.addEventListener("open", () => {
      setLiveConnected(true);
    });

    // Compteur initial / mises à jour
    es.addEventListener("count", (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (typeof data.unread === "number") setUnread(data.unread);
      } catch { /* noop */ }
    });

    // Nouvelle notification
    es.addEventListener("notification", (ev) => {
      try {
        const n = JSON.parse(ev.data);
        const cat = n.category || getCategory(n.type);
        const currentPrefs = prefsRef.current || {};
        const mutedView = (currentPrefs.muted_categories || []).includes(cat);
        const mutedToast = (currentPrefs.muted_toast_categories || []).includes(cat);
        if (!mutedView) {
          setItems((prev) => {
            if (prev.some((x) => x.id === n.id)) return prev;
            return [n, ...prev].slice(0, 200);
          });
        }
        if (!mutedToast) {
          toast(n.title || "Nouvelle notification", {
            description: n.message,
            duration: 5000,
          });
        }
        // Rediffusion globale : permet aux autres vues (Dashboard, etc.) de se rafraîchir
        // en temps réel selon le type de notification reçu.
        try {
          window.dispatchEvent(new CustomEvent("plania:notification", { detail: n }));
        } catch { /* noop */ }
      } catch { /* noop */ }
    });

    // Heartbeat — rien à faire, juste garder la connexion
    es.addEventListener("ping", () => { /* alive */ });

    es.onerror = () => {
      setLiveConnected(false);
      try { es.close(); } catch { /* noop */ }
      sseRef.current = null;
      // Retry après délai
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(openStream, SSE_RETRY_DELAY_MS);
    };
  };

  useEffect(() => {
    loadFull();
    openStream();
    // Filet de sécurité : poll à basse fréquence pour rattraper d'éventuelles déconnexions silencieuses
    pollRef.current = setInterval(loadFull, POLL_FALLBACK_MS);
    // Rafraîchit la cloche dès qu'un autre composant marque des notifications comme lues
    // (ex: auto-marquage lors du changement de rubrique dans Layout).
    const onChange = () => { loadFull(); };
    window.addEventListener("plania:notifications-changed", onChange);
    return () => {
      if (sseRef.current) { try { sseRef.current.close(); } catch { /* noop */ } }
      if (pollRef.current) clearInterval(pollRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      window.removeEventListener("plania:notifications-changed", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAllRead = async () => {
    await api.put("/notifications/mark-all-read");
    loadFull();
    try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-9 w-9 flex items-center justify-center hover:bg-zinc-50 rounded-sm border border-zinc-200"
          data-testid="notifications-bell"
        >
          <Bell size={16} strokeWidth={1.75} className="text-zinc-700" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-mono font-semibold rounded-full flex items-center justify-center"
              data-testid="notifications-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[500px] overflow-y-auto p-0">
        <div className="flex items-center justify-between p-3 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <DropdownMenuLabel className="label-uppercase p-0">Notifications</DropdownMenuLabel>
            <span
              className={`inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${
                liveConnected
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-zinc-50 text-zinc-500 border-zinc-200"
              }`}
              title={liveConnected ? "Connexion temps réel active" : "Mode polling (reconnexion en cours)"}
              data-testid="notifications-live-status"
            >
              {liveConnected ? <Wifi size={9} /> : <WifiOff size={9} />}
              {liveConnected ? "LIVE" : "POLL"}
            </span>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7" data-testid="mark-all-read">
              <CheckCheck size={12} className="mr-1" /> Tout marquer lu
            </Button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500 font-mono">Aucune notification</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors ${n.read ? "opacity-60" : ""}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && (
                    <span className="h-2 w-2 rounded-full bg-[#0055FF] mt-1.5 flex-shrink-0" />
                  )}
                  <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                    <p className="text-sm font-medium text-zinc-900">{n.title}</p>
                    <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">{n.message}</p>
                    <p className="font-mono text-[10px] text-zinc-400 mt-1">
                      {new Date(n.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="border-t border-zinc-100 p-2">
          <Link
            to="/notifications"
            className="flex items-center justify-center gap-2 text-xs text-[#0055FF] hover:text-[#0044CC] font-mono py-2 hover:bg-zinc-50 rounded-sm transition-colors"
            data-testid="link-notifications-center"
          >
            <ExternalLink size={11} /> Ouvrir le centre de notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
